import type { ReactNode } from "react";
import { BANDE, COURBE, DUREE_BANDES, HAUTEURS } from "./guideStyles.js";

/**
 * ============================================================================
 *  GuideLetterbox.tsx — Les bandes haute et basse du mode guide
 * ----------------------------------------------------------------------------
 *  Elles RECOUVRENT le canvas : le viewer n'est jamais redimensionné, et la scène
 *  garde sa géométrie quand les bandes entrent ou sortent. Leur hauteur s'anime
 *  (.8 s) ; leur contenu, lui, garde sa hauteur fixe à l'intérieur et passe en
 *  fondu (.4 s) — il ne s'écrase donc pas pendant le mouvement.
 *
 *  `prefers-reduced-motion` : plus de hauteur animée, un simple fondu.
 *
 *  Les bandes ne captent aucun geste (`pointerEvents`) : seuls les contrôles
 *  qu'elles portent répondent, et on peut prendre la caméra à travers elles.
 * ==========================================================================*/

export interface GuideLetterboxProps {
  visible: boolean;
  etroit: boolean;
  reduit: boolean;
  haut: ReactNode;
  bas: ReactNode;
}

export function GuideLetterbox({ visible, etroit, reduit, haut, bas }: GuideLetterboxProps) {
  const h = etroit ? HAUTEURS.telephone : HAUTEURS.ordinateur;
  return (
    <>
      <Bande cote="haut" hauteur={h.haut} visible={visible} reduit={reduit}>
        {haut}
      </Bande>
      <Bande cote="bas" hauteur={h.bas} visible={visible} reduit={reduit}>
        {bas}
      </Bande>
    </>
  );
}

function Bande({
  cote,
  hauteur,
  visible,
  reduit,
  children,
}: {
  cote: "haut" | "bas";
  hauteur: number;
  visible: boolean;
  reduit: boolean;
  children: ReactNode;
}) {
  const zoneSure = `env(safe-area-inset-${cote === "haut" ? "top" : "bottom"})`;
  const total = `calc(${hauteur}px + ${zoneSure})`;
  return (
    <div
      data-guide-bande={cote}
      style={{
        position: "absolute",
        left: 0,
        right: 0,
        [cote === "haut" ? "top" : "bottom"]: 0,
        background: BANDE,
        overflow: "hidden",
        pointerEvents: "none",
        zIndex: 20,
        // La hauteur ne s'anime pas en mouvement réduit : la bande est là, ou n'y est pas.
        height: reduit ? (visible ? total : 0) : visible ? total : 0,
        transition: reduit ? "none" : `height ${DUREE_BANDES} ${COURBE}`,
      }}
    >
      <div
        aria-hidden={!visible}
        // Hauteur fixe, collée au bord de l'écran : le contenu ne s'écrase pas, il est découvert.
        style={{
          position: "absolute",
          left: 0,
          right: 0,
          [cote === "haut" ? "top" : "bottom"]: 0,
          height: total,
          boxSizing: "border-box",
          [cote === "haut" ? "paddingTop" : "paddingBottom"]: zoneSure,
          opacity: visible ? 1 : 0,
          visibility: visible ? "visible" : "hidden",
          transition: reduit
            ? "opacity .3s ease"
            : `opacity .4s ease ${visible ? ".4s" : "0s"}, visibility 0s linear ${visible ? "0s" : ".4s"}`,
          pointerEvents: visible ? "auto" : "none",
        }}
      >
        {children}
      </div>
    </div>
  );
}
